import { useRef, useState } from "react";
import {
  useListLeadImports,
  useUploadLeadsCsv,
  getLeadImport,
  getListLeadImportsQueryKey,
  getListLeadsQueryKey,
  getGetLeadsSummaryQueryKey,
  type LeadImport,
  type LeadImportRowError,
} from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Upload, Download, FileSpreadsheet } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const SAMPLE_TEMPLATE =
  "mobileNumber,customerName,source,spaceType,seatRangeMin,seatRangeMax,urgency,location,spaceName\n" +
  "9812345670,Sample Lead,organic_website,private_cabin,2,4,medium,Indiranagar Bengaluru,Hub Indiranagar\n";

function escapeCsvField(v: string | number): string {
  const s = String(v);
  if (s.includes(",") || s.includes("\"") || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function downloadFile(filename: string, content: string, mime = "text/csv") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

async function fetchImportErrors(id: number): Promise<LeadImportRowError[]> {
  const data = await getLeadImport(id);
  return data.errors ?? [];
}

export function ImportHistory() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  const { data: imports, isLoading } = useListLeadImports({
    query: { queryKey: getListLeadImportsQueryKey() },
  });

  const uploadMutation = useUploadLeadsCsv({
    mutation: {
      onSuccess: (result) => {
        toast({
          title: "Import processed",
          description:
            `${result.succeededRows} succeeded, ${result.failedRows} failed (of ${result.totalRows} rows)`,
        });
        void queryClient.invalidateQueries({ queryKey: getListLeadImportsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getListLeadsQueryKey() });
        void queryClient.invalidateQueries({ queryKey: getGetLeadsSummaryQueryKey() });
      },
      onError: (err) => {
        const msg = err instanceof Error ? err.message : "Upload failed";
        toast({ title: "Upload failed", description: msg, variant: "destructive" });
      },
    },
  });

  const onPickFile = () => fileInputRef.current?.click();

  const onFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "File too large", description: "Max 5 MB", variant: "destructive" });
      return;
    }
    const text = await file.text();
    uploadMutation.mutate({ data: { filename: file.name, csv: text } });
  };

  const downloadErrors = async (job: LeadImport) => {
    if (job.failedRows === 0) return;
    setDownloadingId(job.id);
    try {
      const errors = await fetchImportErrors(job.id);
      const lines = ["row,message", ...errors.map((er) => `${er.row},${escapeCsvField(er.message)}`)];
      const safeName = job.filename.replace(/\.csv$/i, "");
      downloadFile(`${safeName}-errors-${job.id}.csv`, lines.join("\n"));
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Could not download errors";
      toast({ title: "Download failed", description: msg, variant: "destructive" });
    } finally {
      setDownloadingId(null);
    }
  };

  const downloadTemplate = () => {
    downloadFile("leads-import-template.csv", SAMPLE_TEMPLATE);
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <FileSpreadsheet className="h-5 w-5" /> Lead Imports
            </h2>
            <p className="text-sm text-muted-foreground">
              Upload a CSV of leads. Each upload is recorded here with its success/failure counts.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate}>
              <Download className="mr-2 h-4 w-4" /> Template
            </Button>
            <Button size="sm" onClick={onPickFile} disabled={uploadMutation.isPending}>
              <Upload className="mr-2 h-4 w-4" />
              {uploadMutation.isPending ? "Uploading…" : "Upload CSV"}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={onFileChange}
              data-testid="input-csv-upload"
            />
          </div>
        </div>

        <div className="border rounded-md overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>File</TableHead>
                <TableHead>Uploader</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Succeeded</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead>Errors</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                [...Array(2)].map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}><Skeleton className="h-5 w-20" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : !imports || imports.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground py-6">
                    No imports yet. Upload a CSV to get started.
                  </TableCell>
                </TableRow>
              ) : (
                imports.map((job) => (
                  <TableRow key={job.id}>
                    <TableCell className="whitespace-nowrap text-sm">
                      {new Date(job.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell className="font-medium truncate max-w-[260px]">{job.filename}</TableCell>
                    <TableCell className="text-sm">{job.uploadedByName}</TableCell>
                    <TableCell className="text-right tabular-nums">{job.totalRows}</TableCell>
                    <TableCell className="text-right tabular-nums">
                      <Badge variant="outline" className="bg-green-100 text-green-800 border-transparent">
                        {job.succeededRows}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {job.failedRows > 0 ? (
                        <Badge variant="outline" className="bg-red-100 text-red-800 border-transparent">
                          {job.failedRows}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {job.failedRows > 0 ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadErrors(job)}
                          disabled={downloadingId === job.id}
                          data-testid={`button-download-errors-${job.id}`}
                        >
                          <Download className="mr-1 h-3 w-3" />
                          {downloadingId === job.id ? "Loading…" : "Error report"}
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
